/**
 * CelebrationEffects System
 *
 * Phase 5: Manages all visual and audio celebration effects when reaching victory
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/constants';
import { ICONS } from '../config/icons';
import { zzfx } from 'zzfx';

export class CelebrationEffects {
  private scene: Phaser.Scene;
  private fireworkEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private confettiEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private sparkleEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private victoryText?: Phaser.GameObjects.Text;
  private medalSprite?: Phaser.GameObjects.Image;

  // MEMORY LEAK FIX (v0.8.6): Store infinite tweens for cleanup
  private victoryTextTween?: Phaser.Tweens.Tween;
  private medalTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createParticleTextures();
  }

  private createParticleTextures(): void {
    // Create firework particle texture
    if (!this.scene.textures.exists('firework-particle')) {
      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0xffffff, 1);
      graphics.fillCircle(4, 4, 4);
      graphics.generateTexture('firework-particle', 8, 8);
      graphics.destroy();
    }

    // Create confetti particle texture
    if (!this.scene.textures.exists('confetti-particle')) {
      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0xffffff, 1);
      graphics.fillRect(0, 0, 8, 4);
      graphics.generateTexture('confetti-particle', 8, 4);
      graphics.destroy();
    }

    // Create sparkle particle texture
    if (!this.scene.textures.exists('sparkle-particle')) {
      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0xffffff, 1);
      graphics.fillCircle(3, 3, 3);
      graphics.generateTexture('sparkle-particle', 6, 6);
      graphics.destroy();
    }
  }

  public playVictorySequence(bloboX: number, bloboY: number, completionTime: number): void {
    // Play victory sound (replaces zzfx chime and fanfare)
    this.scene.sound.play('victory-sound', { volume: 0.5 });

    // Play visual effects in sequence
    this.createFireworks(bloboX, bloboY);
    this.createConfetti();
    this.createSparkles(bloboX, bloboY);
    this.showVictoryText();
    this.showMedal(completionTime, bloboX, bloboY);

    // Clean up effects after celebration duration
    this.scene.time.delayedCall(GAME_CONSTANTS.VICTORY.CELEBRATION_DURATION, () => {
      this.cleanup();
    });
  }

  private createFireworks(x: number, y: number): void {
    // Create multiple bursts of fireworks
    const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff];
    const burstCount = GAME_CONSTANTS.VICTORY.FIREWORK_BURSTS;

    for (let i = 0; i < burstCount; i++) {
      this.scene.time.delayedCall(i * 600, () => {
        const color = colors[i % colors.length];
        const emitter = this.scene.add.particles(x, y - 100, 'firework-particle', {
          speed: { min: 100, max: 200 },
          angle: { min: -120, max: -60 },
          scale: { start: 1, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan: 1000,
          frequency: -1,
          quantity: 50,
          tint: color,
          blendMode: 'ADD',
          emitting: false,
        });

        emitter.explode();
        this.fireworkEmitters.push(emitter);

        // Play firework sound
        zzfx(...[, , 400 + i * 100, 0.01, 0.05, 0.3, , 1.5, , , , , , , , , , 0.5, 0.01]);
      });
    }
  }

  private createConfetti(): void {
    // Create falling confetti from top of screen
    const camera = this.scene.cameras.main;
    const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff, 0xffffff];

    // Determine particle count based on platform (mobile optimization)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const particleCount = isMobile ? 2 : 4;

    for (let i = 0; i < 3; i++) {
      const color = colors[i % colors.length];
      const emitter = this.scene.add.particles(
        camera.scrollX + camera.width / 2,
        camera.scrollY,
        'confetti-particle',
        {
          speed: { min: 50, max: 150 },
          angle: { min: 45, max: 135 },
          scale: { min: 0.5, max: 1.5 },
          alpha: { start: 1, end: 0.5 },
          lifespan: 3000,
          frequency: 50,
          quantity: particleCount,
          gravityY: 200,
          tint: color,
          rotate: { min: 0, max: 360 },
          x: { min: camera.scrollX, max: camera.scrollX + camera.width },
          y: camera.scrollY,
        }
      );

      this.confettiEmitters.push(emitter);

      // Stop confetti after duration
      this.scene.time.delayedCall(GAME_CONSTANTS.VICTORY.CONFETTI_DURATION, () => {
        emitter.stop();
      });
    }
  }

  private createSparkles(bloboX: number, bloboY: number): void {
    // Create orbital sparkles around Blobo
    this.sparkleEmitter = this.scene.add.particles(bloboX, bloboY, 'sparkle-particle', {
      speed: 30,
      angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 1000,
      frequency: 50,
      quantity: 2,
      tint: 0xffd700,
      blendMode: 'ADD',
    });
  }

  private showVictoryText(): void {
    const camera = this.scene.cameras.main;
    const centerX = camera.scrollX + camera.width / 2;
    const centerY = camera.scrollY + camera.height / 2 - 100;

    this.victoryText = this.scene.add.text(centerX, centerY, 'VICTORY!', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: '64px',
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 8,
    });
    this.victoryText.setOrigin(0.5);
    this.victoryText.setAlpha(0);
    this.victoryText.setScale(0.5);
    this.victoryText.setScrollFactor(0); // Fixed to camera

    // Animate victory text
    this.scene.tweens.add({
      targets: this.victoryText,
      alpha: 1,
      scale: 1.2,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Add bounce animation (MEMORY LEAK FIX: store tween for cleanup)
        this.victoryTextTween = this.scene.tweens.add({
          targets: this.victoryText,
          scale: { from: 1.2, to: 1.3 },
          duration: 800,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });
  }

  private showMedal(completionTime: number, bloboX: number, bloboY: number): void {
    // Get medal type and use colored SVG
    const medalType = this.getMedalType(completionTime);

    // Medal icons based on type
    const medalIcons = {
      gold: ICONS.MEDAL_GOLD,
      silver: ICONS.MEDAL_SILVER,
      bronze: ICONS.MEDAL_BRONZE,
    };
    const medalIconKey = medalIcons[medalType];

    // Create medal as an image sprite (using colored SVG)
    this.medalSprite = this.scene.add.image(bloboX, bloboY - 80, medalIconKey);
    this.medalSprite.setDisplaySize(48, 48);
    this.medalSprite.setOrigin(0.5);
    this.medalSprite.setScale(0);

    // Animate medal appearance
    this.scene.tweens.add({
      targets: this.medalSprite,
      scale: 1.5,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Gentle floating animation (MEMORY LEAK FIX: store tween for cleanup)
        this.medalTween = this.scene.tweens.add({
          targets: this.medalSprite,
          y: this.medalSprite!.y - 10,
          duration: 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });
  }

  private getMedalType(completionTime: number): 'gold' | 'silver' | 'bronze' {
    if (completionTime < GAME_CONSTANTS.VICTORY.MEDAL_THRESHOLDS.GOLD) {
      return 'gold';
    } else if (completionTime < GAME_CONSTANTS.VICTORY.MEDAL_THRESHOLDS.SILVER) {
      return 'silver';
    } else {
      return 'bronze';
    }
  }

  private cleanup(): void {
    this.fireworkEmitters.forEach(emitter => emitter.destroy());
    this.confettiEmitters.forEach(emitter => emitter.destroy());
    this.sparkleEmitter?.destroy();

    // MEMORY LEAK FIX (v0.8.6): Stop infinite tweens to prevent CPU drain
    if (this.victoryTextTween) {
      this.victoryTextTween.stop();
      this.victoryTextTween = undefined;
    }
    if (this.medalTween) {
      this.medalTween.stop();
      this.medalTween = undefined;
    }

    // Keep victory text and medal visible (but stop animating them)
  }

  public destroy(): void {
    this.cleanup();
    this.victoryText?.destroy();
    this.medalSprite?.destroy();
  }
}
